import moment from 'moment';
import { dateFormatter } from 'helpers/dateFormatter';
import { STEPS_LABELS, stepsStatuses } from 'constants/constants';
import { OPENING_STATUSES } from 'constants/opening';
import { ROLES } from 'permission';
import type { DashboardOpeningsGroupRow } from '_templates/dashboardTypes';
import { BUTTON_LABELS, CANDIDATES_GROUP, OPENINGS_GROUP } from './constants';

export const useTabs = (disabled: boolean) => [
  {
    label: 'Openings',
    key: 'openings',
    disabled,
  },
  {
    label: 'Active candidates',
    key: 'candidates',
    disabled,
  },
  {
    label: 'Self-submitted candidates',
    key: 'ssc',
    disabled,
  },
];

export const isServiceGrouping = (grouping: string) => grouping === OPENINGS_GROUP[0].value;

export const serviceTableDataMapper = (services: DashboardOpeningsGroupRow[]) =>
  services.map(service => ({
    uuid: service.uuid,
    name: service.name,
    count: service.openings_count,
    candidates_count: service.candidates_count,
    openings_count: service.total_openings_count,
    openings: service.openings.map(opening => ({
      uuid: opening.uuid,
      job: {
        job_name: opening.job_name,
        job_uuid: opening.job_uuid,
        priority: opening.priority,
      },
      level: opening.custom_level || opening.level,
      creation_date: dateFormatter(opening.created),
      expected_date: {
        date: dateFormatter(opening.expected as string),
        error:
          opening.status === OPENING_STATUSES.OPEN &&
          moment(opening.expected as string, 'YYYY-MM-DD')
            .startOf('day')
            .isSameOrBefore(moment().startOf('day')),
      },
      rm_approval: opening.rm_approval_date ? dateFormatter(opening.rm_approval_date) : '-',
      lm_approval: opening.lm_approved_at
        ? opening.lm_approved_at === '-'
          ? '-'
          : dateFormatter(opening.lm_approved_at)
        : 'N/A',
      team_lead: opening.team_lead.full_name,
      status: opening.status,
      action: {
        status: opening.status,
        job_uuid: opening.job_uuid,
        job_name: opening.job_name,
        service_uuid: opening.uuid,
        opening,
      },
    })),
  }));

export const jobTableDataMapper = (jobs: DashboardOpeningsGroupRow[]) =>
  jobs.map(job => ({
    uuid: job.uuid,
    name: job.name,
    count: job.openings_count,
    service: job.service_name,
    candidates_count: job.candidates_count,
    openings_count: job.total_openings_count,
    openings: job.openings.map(opening => ({
      uuid: opening.uuid,
      level: { priority: opening.priority, level: opening.custom_level || opening.level },
      creation_date: dateFormatter(opening.created),
      expected_date: {
        date: dateFormatter(opening.expected as string),
        error:
          opening.status === OPENING_STATUSES.OPEN &&
          moment(opening.expected as string, 'YYYY-MM-DD')
            .startOf('day')
            .isSameOrBefore(moment().startOf('day')),
      },
      rm_approval: opening.rm_approval_date ? dateFormatter(opening.rm_approval_date) : '-',
      lm_approval: opening.lm_approved_at
        ? opening.lm_approved_at === '-'
          ? '-'
          : dateFormatter(opening.lm_approved_at)
        : 'N/A',
      team_lead: opening.team_lead.full_name,
      status: opening.status,
      action: {
        status: opening.status,
        job_uuid: job.uuid,
        job_name: job.name,
        service_uuid: opening.uuid,
        opening,
      },
    })),
  }));

const getAction = (c: Record<string, unknown>, user: Record<string, unknown>) => {
  const assignment = c.assignment as Record<string, unknown> | undefined;
  const activeRole = (user.active_role as Record<string, unknown>).name as string;

  const decisionActionStatuses = [
    stepsStatuses.waitingForFinalDecision,
    stepsStatuses.scheduleInterview,
    stepsStatuses.sendOffer,
  ] as const;

  return (decisionActionStatuses as readonly string[]).includes((assignment?.status as string) ?? '')
    ? {
        text: '',
        subtext: '',
        time: '',
        button: (
          activeRole === ROLES.INTERVIEWER || activeRole === ROLES.TEAM_LEAD || activeRole === ROLES.SERVICE_HEAD
            ? c.is_action_required
            : true
        )
          ? {
              label: BUTTON_LABELS[assignment!.status as string],
              isFinalDecision: stepsStatuses.waitingForFinalDecision === assignment!.status,
            }
          : undefined,
      }
    : stepsStatuses.provideFeedback === assignment?.status
    ? (activeRole === ROLES.INTERVIEWER || activeRole === ROLES.TEAM_LEAD || activeRole === ROLES.SERVICE_HEAD
        ? c.is_action_required
        : true) &&
      c.feedback &&
      (!(c.feedback as Record<string, unknown>).posted_at || (c.feedback as Record<string, unknown>).is_draft)
      ? {
          text: '',
          subtext: '',
          time: '',
          button: {
            label: BUTTON_LABELS[assignment!.status as string],
          },
        }
      : {
          text: '',
          subtext: '',
          time: '',
          button: '',
        }
    : stepsStatuses.waitForResolution === assignment?.status
    ? c.start_date &&
      moment(c.start_date as string, 'YYYY-MM-DD')
        .startOf('day')
        .isAfter(moment().startOf('day'))
      ? {
          text: 'Entry date:',
          subtext: moment(c.start_date as string, 'YYYY-MM-DD').format('MMM DD, YYYY'),
          time: '',
          button: '',
        }
      : {
          text: '',
          subtext: '',
          time: '',
          button: (
            activeRole === ROLES.INTERVIEWER || activeRole === ROLES.TEAM_LEAD || activeRole === ROLES.SERVICE_HEAD
              ? c.is_action_required
              : true
          )
            ? {
                label: BUTTON_LABELS[assignment!.status as string],
                isFinalDecision: stepsStatuses.waitingForFinalDecision === (assignment!.status as string),
              }
            : undefined,
        }
    : c.expected_answer_date
    ? moment(c.expected_answer_date as string, 'YYYY-MM-DD')
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
      ? {
          text: '',
          subtext: '',
          time: '',
          button: {
            label: BUTTON_LABELS[assignment!.status as string],
            isFinalDecision: stepsStatuses.waitingForFinalDecision === assignment!.status,
          },
        }
      : {
          text: 'Expected answer date:',
          subtext: moment(c.expected_answer_date as string).format('MMM DD, YYYY'),
          time: '',
          button: '',
        }
    : c.interview
    ? {
        text: moment((c.interview as Record<string, unknown>).date as string).format('ddd MMM DD, YYYY'),
        subtext: `${dateFormatter(
          `${(c.interview as Record<string, unknown>).date} ${(c.interview as Record<string, unknown>).from_time}`,
          'H:mm'
        )} - ${dateFormatter(
          `${(c.interview as Record<string, unknown>).date} ${(c.interview as Record<string, unknown>).to_time}`,
          'H:mm'
        )}`,
        time: '',
        link: (c.interview as Record<string, unknown>).link,
        button: '',
      }
    : c.reminder
    ? {
        text: `Reminder: ${(c.reminder as Record<string, unknown>).type}`,
        subtext: '',
        time: moment((c.reminder as Record<string, unknown>).datetime as string).format('ddd MMM DD, YYYY [at] H:mm'),
        button: '',
      }
    : {
        text: '',
        subtext: '',
        time: '',
        button: '',
      };
};

export const candidatesTableDataMapper = (candidates: Record<string, unknown>[], user: Record<string, unknown>) =>
  candidates.map((c, index) => ({
    uuid: (c.uuid as string) + index,
    candidate: {
      uuid: c.uuid,
      url: (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid
        ? `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
            (c.assignment as Record<string, unknown>).step_uuid
          }/${c.uuid}`
        : `/candidates/${c.uuid}/show`,
      full_name_latin: c.full_name_latin,
      full_name_local: c.full_name_local,
      hot: false,
    },
    offer_decision: (c.assignment as Record<string, unknown> | undefined)?.offer_decision,
    opening: (c.assignment as Record<string, unknown> | undefined)?.opening,
    level: c.level,
    job: {
      job_name: (c.assignment as Record<string, unknown> | undefined)?.job_name,
      service: (c.assignment as Record<string, unknown> | undefined)?.service,
      uuid: (c.assignment as Record<string, unknown> | undefined)?.job_uuid,
      step_uuid: (c.assignment as Record<string, unknown> | undefined)?.step_uuid,
    },
    recruiter: {
      full_name: c.recruiter_full_name,
      uuid: c.recruiter_uuid,
    },
    cvs: (c.cvs as string[]).map(uuid => ({ file: uuid, uuid })),
    cvs_links: c.cvs_links,
    status_step: {
      status: (c.assignment as Record<string, unknown> | undefined)?.status,
      step_number:
        Object.values(STEPS_LABELS).findIndex(
          l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
        ) + 1,
      step:
        (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid &&
        `${
          Object.values(STEPS_LABELS).findIndex(
            l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
          ) + 1
        } - ${(c.assignment as Record<string, unknown>).step_type}`,
    },
    feedback: c.feedback,
    action: {
      recruiter_uuid: c.recruiter_uuid,
      ...getAction(c, user),
    },
  }));

export const candidatesActionTableDataMapper = (actions: Record<string, unknown>[], user: Record<string, unknown>) =>
  actions.map((a, index) => ({
    uuid: index,
    name: a.group_name,
    count: a.candidates_count,
    candidates: (a.candidates as Record<string, unknown>[]).map(c => ({
      uuid: c.uuid,
      candidate: {
        uuid: c.uuid,
        url: (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid
          ? `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
              (c.assignment as Record<string, unknown>).step_uuid
            }/${c.uuid}`
          : `/candidates/${c.uuid}/show`,
        full_name_latin: c.full_name_latin,
        full_name_local: c.full_name_local,
        hot: false,
      },
      level: c.level,
      job: {
        job_name: (c.assignment as Record<string, unknown> | undefined)?.job_name,
        service: (c.assignment as Record<string, unknown> | undefined)?.service,
        uuid: (c.assignment as Record<string, unknown> | undefined)?.job_uuid,
        step_uuid: (c.assignment as Record<string, unknown> | undefined)?.step_uuid,
      },
      recruiter: {
        full_name: c.recruiter_full_name,
        uuid: c.recruiter_uuid,
      },
      cvs: (c.cvs as string[]).map(uuid => ({ file: uuid, uuid })),
      cvs_links: c.cvs_links,
      status_step: {
        status: (c.assignment as Record<string, unknown> | undefined)?.status,
        step_number:
          Object.values(STEPS_LABELS).findIndex(
            l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
          ) + 1,
        step:
          (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid &&
          `${
            Object.values(STEPS_LABELS).findIndex(
              l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
            ) + 1
          } - ${(c.assignment as Record<string, unknown>).step_type}`,
      },
      action: {
        recruiter_uuid: c.recruiter_uuid,
        ...getAction(c, user),
      },
    })),
  }));

export const candidatesJobTableDataMapper = (actions: Record<string, unknown>[], user: Record<string, unknown>) =>
  actions.map((a, index) => ({
    uuid: index,
    name: a.group_name || 'Job not assigned',
    extra: a.service_name,
    count: a.candidates_count,
    candidates: (a.candidates as Record<string, unknown>[]).map(c => ({
      uuid: c.uuid,
      candidate: {
        uuid: c.uuid,
        url: (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid
          ? `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
              (c.assignment as Record<string, unknown>).step_uuid
            }/${c.uuid}`
          : `/candidates/${c.uuid}/show`,
        full_name_latin: c.full_name_latin,
        full_name_local: c.full_name_local,
        hot: false,
      },
      level: c.level,
      job: {
        job_name: (c.assignment as Record<string, unknown> | undefined)?.job_name,
        service: (c.assignment as Record<string, unknown> | undefined)?.service,
        uuid: (c.assignment as Record<string, unknown> | undefined)?.job_uuid,
        step_uuid: (c.assignment as Record<string, unknown> | undefined)?.step_uuid,
      },
      recruiter: {
        full_name: c.recruiter_full_name,
        uuid: c.recruiter_uuid,
      },
      cvs: (c.cvs as string[]).map(uuid => ({ file: uuid, uuid })),
      cvs_links: c.cvs_links,
      status_step: {
        status: (c.assignment as Record<string, unknown> | undefined)?.status,
        step_number:
          Object.values(STEPS_LABELS).findIndex(
            l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
          ) + 1,
        step:
          (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid &&
          `${
            Object.values(STEPS_LABELS).findIndex(l => l === (c.assignment as Record<string, unknown>).step_type) + 1
          } - ${(c.assignment as Record<string, unknown>).step_type}`,
      },
      action: {
        recruiter_uuid: c.recruiter_uuid,
        ...getAction(c, user),
      },
    })),
  }));

export const candidatesStepTableDataMapper = (actions: Record<string, unknown>[], user: Record<string, unknown>) =>
  actions.map((a, index) => ({
    uuid: index,
    name: a.group_name || 'No step',
    count: a.candidates_count,
    candidates: (a.candidates as Record<string, unknown>[]).map(c => ({
      uuid: c.uuid,
      candidate: {
        uuid: c.uuid,
        url: (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid
          ? `/jobs/${(c.assignment as Record<string, unknown>).job_uuid}/show/steps/${
              (c.assignment as Record<string, unknown>).step_uuid
            }/${c.uuid}`
          : `/candidates/${c.uuid}/show`,
        full_name_latin: c.full_name_latin,
        full_name_local: c.full_name_local,
        hot: false,
      },
      level: c.level,
      job: {
        job_name: (c.assignment as Record<string, unknown> | undefined)?.job_name,
        service: (c.assignment as Record<string, unknown> | undefined)?.service,
        uuid: (c.assignment as Record<string, unknown> | undefined)?.job_uuid,
        step_uuid: (c.assignment as Record<string, unknown> | undefined)?.step_uuid,
      },
      recruiter: {
        full_name: c.recruiter_full_name,
        uuid: c.recruiter_uuid,
      },
      cvs: (c.cvs as string[]).map(uuid => ({ file: uuid, uuid })),
      cvs_links: c.cvs_links,
      status_step: {
        status: (c.assignment as Record<string, unknown> | undefined)?.status,
        step_number:
          Object.values(STEPS_LABELS).findIndex(
            l => l === (c.assignment as Record<string, unknown> | undefined)?.step_type
          ) + 1,
        step:
          (c.assignment as Record<string, unknown> | undefined)?.job_assignment_uuid &&
          `${
            Object.values(STEPS_LABELS).findIndex(l => l === (c.assignment as Record<string, unknown>).step_type) + 1
          } - ${(c.assignment as Record<string, unknown>).step_type}`,
      },
      action: {
        recruiter_uuid: c.recruiter_uuid,
        ...getAction(c, user),
      },
    })),
  }));

export const CANDIDATES_TABLES_MAPPERS: Record<
  string,
  (results: Record<string, unknown>[], user: Record<string, unknown>) => Record<string, unknown>[]
> = {
  [CANDIDATES_GROUP[0].value]: candidatesTableDataMapper,
  [CANDIDATES_GROUP[1].value]: candidatesActionTableDataMapper,
  [CANDIDATES_GROUP[2].value]: candidatesJobTableDataMapper,
  [CANDIDATES_GROUP[3].value]: candidatesStepTableDataMapper,
};
